// src/antdviews/UserAccountDetailView.tsx

// 用户账号详情页面（Ant Design 风格）

import axios from 'axios'
import React from 'react'
import { withTranslation, WithTranslation } from 'react-i18next'
import { Layout, message } from 'antd'

import Header from './components/Header'
import { FilePath, UserMgmtApi } from '../services/Api'
import {
    getSourceName,
    LOG_SOURCE,
    NEW_RECORD_SYSTEM,
    RECORD_LOCATION,
    REPORT_TYPE,
    ROLE,
} from '../services/Macro'
import { checker } from '../utils/Checker'
import AccountDetailContent from './components/AccountDetailContent'

// ⬇️ 直接复用子组件里的类型定义，保证两边完全一致，从此不再有字段不匹配
import type {
    DetailData as AccountDetail,
    UserItem,
    LogItem,
    RecordItem,
    PhoneInfo,
} from './components/AccountDetailContent'

import UserSidebar from './components/UserSidebar'
import { strUtil } from '../utils/StrUtil'
import { timeUtil } from '../utils/TimeUtil'
import { encrypUtil } from '../utils/EncrypUtil'
import { Global } from '../services/Global'
import WarnModal from '../components/WarnModal'
import ConfirmModal from '../components/ConfirmModal'
import { Page } from '../services/Page'

// ⬇️ 保持本文件原有的对外导出路径不变（放在所有 import 之后，避免 import/first 报错）
export type {
    AccountDetail,
    UserItem,
    LogItem,
    RecordItem,
    PhoneInfo,
}

const { Content } = Layout

/** 弹窗统一样式（若仍使用 react-modal 风格组件） */
const warnModalStyles = {
    content: {
        width: '480px',
        top: '40%',
        left: '50%',
        right: 'auto',
        bottom: 'auto',
        marginRight: '-50%',
        transform: 'translate(-50%, -50%)',
        border: '-20px',
    },
}

/** 确认操作回调类型（保持与子组件 prop 兼容） */
export type ConfirmOp = (...args: any[]) => void

/** 组件 State */
interface UserAccountDetailViewState {
    init: boolean
    showWarn: boolean
    showConfirm: boolean
    warnTitle: string
    warnInfo: string
    confirmOp: ConfirmOp
    detail: AccountDetail
    loginName: string
    recordSize: number
    recordPage: number
    logSize: number
    logPage: number
}

/** 组件 Props（继承 WithTranslation） */
type UserAccountDetailViewProps = WithTranslation

class UserAccountDetailView extends React.Component<
    UserAccountDetailViewProps,
    UserAccountDetailViewState
> {
    state: UserAccountDetailViewState = {
        init: false,
        showWarn: false,
        showConfirm: false,
        warnTitle: '本地错误',
        warnInfo: '参数错误',
        confirmOp: () => {
            console.log('do nothing!')
        },
        detail: {
            isActive: false,
            accountName: '',
            accountEmail: '',
            onlinePortrait: null,
            users: { list: [] as UserItem[], totalPages: 0, pages: [] },
            logs: { list: [] as LogItem[], totalPages: 0, pages: [] },
            records: { list: [] as RecordItem[], totalPages: 0, pages: [] },
            phoneInfo: {
                phoneMode: '',
                phoneManufacturer: '',
                phoneVersion: '',
                phoneSDK: '',
                hasPhoneMode: false,
                hasPhoneManufacturer: false,
                hasPhoneVersion: false,
                hasPhoneSDK: false,
            },
        },
        loginName: '',
        recordSize: 10,
        recordPage: 1,
        logSize: 10,
        logPage: 1,
    }

    componentDidMount(): void {
        const DEBUG = true
        const TAG = `UserAccountDetailView.componentDidMount() - `
        const { t } = this.props
        DEBUG && console.log(TAG, `进入账号详情页面时的登录校验`)

        const callChecker = async (): Promise<void> => {
            DEBUG && console.log(TAG, `数据校验`)
            const token = localStorage.getItem('token') || null
            const lang = localStorage.getItem('lang') || 'zh_CN'
            const isLogin = await checker.checkLogin(ROLE.USER, Page.userAccountMgmt)
            if (!isLogin) return

            // 请求页面数据
            const requestData = {
                role: ROLE.USER,
                token,
                lang: lang.split('_')[0],
            }
            DEBUG && console.log(TAG, `requestData:`, requestData)
            const params = await encrypUtil.encodeRequestRsa(requestData)
            DEBUG && console.log(TAG, `params:\n`, params)

            const api = NEW_RECORD_SYSTEM
                ? UserMgmtApi.USER_GET_ACCOUNT_DETAIL_2
                : UserMgmtApi.USER_GET_ACCOUNT_DETAIL
            const response = await axios.post(api, params)
            DEBUG && console.log(TAG, `获取账号详情:\n`, response.data.data)

            // 处理用户列表
            const users: AccountDetail['users'] = response.data.data.users
            for (let i = 0; i < users.list.length; i++) {
                const user = users.list[i]
                user.created = strUtil.formatTime(user.created as unknown as number)
                user.genderStr = user.gender === 0 ? t('male') : t('female')
                // 处理名字连接
                if (user.middleName) user.name += ` ${user.middleName}`
                if (user.lastName) user.name += ` ${user.lastName}`
                // 处理生日连接
                user.birthday = ''
                user.age = 0
                if (user.yearBirth) {
                    user.birthday += `${user.yearBirth}`
                    if (user.monthBirth) {
                        user.birthday += `-${strUtil.fillPrev(`${user.monthBirth}`, 2, '0')}`
                        if (user.dayBirth) {
                            user.birthday += `-${strUtil.fillPrev(`${user.dayBirth}`, 2, '0')}`
                            user.age = timeUtil.getAgeFromBirth(user.birthday)
                        }
                    }
                }
            }

            const records: AccountDetail['records'] = response.data.data.records
            this.handleRecords(records)

            const logs: AccountDetail['logs'] = response.data.data.logs
            this.handleLogs(logs)

            const accountName: string = response.data.data.accountName
            const accountEmail: string = response.data.data.accountEmail
            const isActive: boolean = response.data.data.isActive
            const onlinePortrait: string | null = response.data.data.onlinePortrait
            let phoneInfo: PhoneInfo = response.data.data.phoneInfo

            if (!phoneInfo) {
                phoneInfo = {
                    phoneMode: t('unknown'),
                    phoneManufacturer: t('unknown'),
                    phoneVersion: t('unknown'),
                    phoneSDK: t('unknown'),
                    hasPhoneMode: false,
                    hasPhoneManufacturer: false,
                    hasPhoneVersion: false,
                    hasPhoneSDK: false,
                }
            } else {
                phoneInfo.hasPhoneMode = !!phoneInfo.phoneMode
                if (!phoneInfo.hasPhoneMode) phoneInfo.phoneMode = t('unknown')
                phoneInfo.hasPhoneManufacturer = !!phoneInfo.phoneManufacturer
                if (!phoneInfo.hasPhoneManufacturer) phoneInfo.phoneManufacturer = t('unknown')
                phoneInfo.hasPhoneVersion = !!phoneInfo.phoneVersion
                if (!phoneInfo.hasPhoneVersion) phoneInfo.phoneVersion = t('unknown')
                phoneInfo.hasPhoneSDK = !!phoneInfo.phoneSDK
                if (!phoneInfo.hasPhoneSDK) phoneInfo.phoneSDK = t('unknown')
            }

            this.setState({
                loginName: accountName,
                detail: {
                    isActive,
                    onlinePortrait,
                    users,
                    logs,
                    records,
                    accountName,
                    accountEmail,
                    phoneInfo,
                },
            })
        }

        if (!this.state.init) {
            callChecker()
        }
        this.setState({ init: true })
    }

    /**
     * 处理记录列表数据
     */
    private handleRecords = (records: AccountDetail['records']): void => {
        const recordPages: number[] = []
        for (let i = 0; i < (records.totalPages || 0); i++) {
            recordPages.push(i + 1)
        }
        records.pages = recordPages

        for (let i = 0; i < records.list.length; i++) {
            const record = records.list[i]
            record.size = strUtil.toM(record.size as unknown as number)
            record.nickname = ''
            record.timeLength = '00:00:00'

            let firmVersion = '()'
            let gn: string | null = null
            let exportFileName: string | null = null

            if (record.meta) {
                if (record.meta.exportFileName && record.meta.exportFileName !== '') {
                    exportFileName = record.meta.exportFileName
                }
                if (record.meta.timeLength) {
                    record.timeLength = strUtil.formatDuration(record.meta.timeLength)
                }
                // 拼接 name + middleName + lastName
                if (record.meta.name) record.nickname = record.meta.name
                if (record.meta.middleName) record.nickname += ` ${record.meta.middleName}`
                if (record.meta.lastName) record.nickname += ` ${record.meta.lastName}`

                if (record.meta.startTime) {
                    record.startTime = strUtil.formatTime(record.meta.startTime * 1000)
                }
                if (record.meta.gn) gn = `${record.meta.gn}`
                if (record.meta.firmwareVersion) {
                    firmVersion = `${gn == null ? '' : '_' + gn}(${record.meta.firmwareVersion
                        .replace('GHealth_DEMO_', '')
                        .replace('_DRV_LIB', '')})`
                }
                if (record.meta.field05) record.description = record.meta.field05
            }

            if (exportFileName) {
                record.fileName = exportFileName
            } else {
                record.fileName = `HMApp_Record_${record.rate}_${strUtil.formatTime(
                    record.startTime as unknown as number,
                    'YYYYMMDD-HHmm-ss',
                )}${firmVersion}`
            }

            record.reports = []
            if ((record.reportTypes & (1 << (REPORT_TYPE.SLEEP - 1))) !== 0) {
                record.reports.push({ name: 'sleep', type: REPORT_TYPE.SLEEP })
            }

            // 指明来源
            record.sourceStr = getSourceName(record.source)

            // 拼接链接地址
            if (record.location === RECORD_LOCATION.DATA_FILES) {
                record.href = `${URL}/download.do?apikey=1F192D3A136F4DAF9B6A4E3E07893E2D&url=${record.url}`
            } else if (record.location === RECORD_LOCATION.UPLOADS_RECORDS) {
                const shorUrl = record.url.slice(12, 12 + 32)
                record.href = `${URL}/admin/records/${shorUrl}`
            } else if (record.location === RECORD_LOCATION.FILES_RECORDS) {
                const shorUrl = record.url.slice(12, 12 + 32)
                record.href = `${FilePath.RECORDS}${shorUrl}`
            }
        }
    }

    /**
     * 处理日志列表数据
     */
    private handleLogs = (logs: AccountDetail['logs']): void => {
        const logPages: number[] = []
        for (let i = 0; i < (logs.totalPages || 0); i++) {
            logPages.push(i + 1)
        }
        logs.pages = logPages

        for (let i = 0; i < logs.list.length; i++) {
            const log = logs.list[i]
            const date = `${log.date.split('-').join('')}`
            const sourceStr = log.source === LOG_SOURCE.ANDROID ? '' : `_${log.source}`
            log.filePath = `${log.uuid}${sourceStr}_${date}.log`
            log.fileName = `HMApp_app_${date}.log`
            log.size = strUtil.toM(log.size as unknown as number)
            log.updateTime = strUtil.formatTime(log.updateTime as unknown as number)
            log.rowspan = 1
            log.hideDate = false

            if (i > 0) {
                if (log.date === logs.list[i - 1].date) {
                    logs.list[i - 1].rowspan = 2
                    log.hideDate = true
                }
            }
        }
    }

    /**
     * 切换记录列表分页
     */
    changeRecordList = async (size: number, page: number): Promise<void> => {
        const DEBUG = true
        const TAG = `UserAccountDetailView.changeRecordList() - `
        this.setState({ recordSize: size, recordPage: page })

        // 仅请求账户的记录列表
        const searchParams = new URLSearchParams(window.location.search)
        const uuid = searchParams.get('uuid')
        const token = localStorage.getItem('token') || null
        const lang = localStorage.getItem('lang') || 'zh_CN'

        const requestData = {
            role: ROLE.USER,
            token,
            uuid,
            lang: lang.split('_')[0],
            page,
            size,
        }
        DEBUG && console.log(TAG, `requestData:\n`, requestData)
        const params = await encrypUtil.encodeRequestRsa(requestData)
        DEBUG && console.log(TAG, `params:\n`, params)

        const response = await axios.post(UserMgmtApi.USER_GET_RECORD_LIST, params)
        DEBUG && console.log(TAG, `获取记录列表:\n`, response.data.data)

        const records: AccountDetail['records'] = response.data.data
        this.handleRecords(records)

        this.setState((prev) => ({
            loginName: prev.loginName,
            detail: {
                ...prev.detail,
                records,
            },
        }))
    }

    /**
     * 切换日志列表分页
     */
    changeLogList = async (size: number, page: number): Promise<void> => {
        const DEBUG = true
        const TAG = `AccountMgmtView.changeLogList() - `
        this.setState({ logSize: size, logPage: page })

        // 仅请求账户的日志列表
        const searchParams = new URLSearchParams(window.location.search)
        const userId = searchParams.get('userId')
        const uuid = searchParams.get('uuid')
        const token = localStorage.getItem('token') || null
        const lang = localStorage.getItem('lang') || 'zh_CN'

        const requestData = {
            role: ROLE.USER,
            token,
            accountId: userId,
            uuid,
            lang: lang.split('_')[0],
            page,
            size,
        }
        DEBUG && console.log(TAG, `requestData:\n`, requestData)
        const params = await encrypUtil.encodeRequestRsa(requestData)
        DEBUG && console.log(TAG, `params:\n`, params)

        const response = await axios.post(UserMgmtApi.USER_GET_LOG_LIST, params)
        DEBUG && console.log(TAG, `获取日志列表:\n`, response.data.data)

        const logs: AccountDetail['logs'] = response.data.data
        this.handleLogs(logs)

        this.setState((prev) => ({
            loginName: prev.loginName,
            detail: {
                ...prev.detail,
                logs,
            },
        }))
    }

    /**
     * 语言切换时的字段更新
     */
    changeLang = (): void => {
        const { t } = this.props

        // 列表中的性别字段切换语言
        const users = this.state.detail.users
        for (let i = 0; i < users.list.length; i++) {
            const user = users.list[i]
            user.genderStr = user.gender === 0 ? t('male') : t('female')
        }

        // 用户概览中如果设备信息未知则需要切换语言
        const phoneInfo = this.state.detail.phoneInfo
        if (!phoneInfo.hasPhoneMode) phoneInfo.phoneMode = t('unknown')
        if (!phoneInfo.hasPhoneManufacturer) phoneInfo.phoneManufacturer = t('unknown')
        if (!phoneInfo.hasPhoneVersion) phoneInfo.phoneVersion = t('unknown')
        if (!phoneInfo.hasPhoneSDK) phoneInfo.phoneSDK = t('unknown')

        this.setState({
            detail: {
                ...this.state.detail,
                users,
                phoneInfo,
            },
        })
    }

    /**
     * 打开确认弹窗
     * 注意：第三参放宽为 Function，以兼容子组件 AccountDetailContent 的 prop 类型
     */
    openConfirm = (title: string, info: string, op: Function): void => {
        this.setState({
            warnTitle: title,
            warnInfo: info,
            showConfirm: true,
            confirmOp: () => {
                op()
                this.closeConfirm()
            },
        })
    }

    closeConfirm = (): void => {
        this.setState({ showConfirm: false })
    }

    /**
     * 打开错误弹窗
     */
    handleError = (title: string, info: string): void => {
        this.setState({
            warnTitle: title,
            warnInfo: info,
            showWarn: true,
        })
    }

    /**
     * Ant Design 消息提示
     */
    notify = (msg: string): void => {
        message.success(msg)
    }

    closeWarn = (): void => {
        this.setState({ showWarn: false })
    }

    render(): React.ReactNode {
        const { t, i18n } = this.props
        const { detail, loginName, showWarn, showConfirm, warnTitle, warnInfo } = this.state

        return (
            <Layout className="body" style={{ minHeight: '100vh' }}>
                {/* 错误提示弹窗 */}
                <WarnModal
                    isMobile={Global.isMobile}
                    isOpen={showWarn}
                    onRequestClose={this.closeWarn}
                    title={t('error')}
                    subtitle={warnTitle}
                    confirm={t('confirm')}
                    customStyles={warnModalStyles}
                >
                    {warnInfo}
                </WarnModal>

                {/* 确认弹窗 */}
                <ConfirmModal
                    isMobile={Global.isMobile}
                    isOpen={showConfirm}
                    onRequestConfirm={this.state.confirmOp}
                    onRequestCancel={this.closeConfirm}
                    title={t('warning')}
                    subtitle={warnTitle}
                    confirm={t('confirm')}
                    cancel={t('cancel')}
                    customStyles={warnModalStyles}
                >
                    {warnInfo}
                </ConfirmModal>

                {/* 顶部 Header */}
                <Header
                    trans={t}
                    i18n={i18n}
                    loginName={loginName}
                    changeLang={this.changeLang}
                />

                <Layout>
                    {/* 侧边栏 */}
                    <UserSidebar selectMenu={Page.accountMgmt} trans={t} />

                    {/* 主内容区 */}
                    <Content>
                        <AccountDetailContent
                            detail={detail}
                            trans={t}
                            handleConfirm={this.openConfirm}
                            handleError={this.handleError}
                            notify={this.notify}
                            changeRecordList={this.changeRecordList}
                            changeLogList={this.changeLogList}
                        />
                    </Content>
                </Layout>
            </Layout>
        )
    }
}

export const UserAccountDetailPage = withTranslation()(UserAccountDetailView)